import DynamicModal from 'components/DynamicModal';
import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Button } from 'reactstrap';

import {
  IntegrationItem,
  IntegrationOptionsData,
} from '../../types/integrationListing.types';

interface BenchmarkOption {
  label: string;
  value: string;
}

interface ManualScanModalProps {
  isOpen: boolean;
  toggle: () => void;
  row: IntegrationItem;
  optionsData: IntegrationOptionsData;
  onConfirm: (
    selectedRegion: string,
    selectedBenchmarkValues: string[],
    selectedBenchmarkNames: string[],
  ) => void;
}

const ManualScanModal: React.FC<ManualScanModalProps> = ({
  isOpen,
  toggle,
  row,
  optionsData,
  onConfirm,
}) => {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<
    BenchmarkOption[]
  >([]);

  const validRegions = useMemo(() => {
    const pCode = row.provider_code?.toLowerCase() || '';
    const providerRegionsObj = optionsData.regions.find((r) =>
      Object.keys(r).some((k) => k.toLowerCase() === pCode),
    );
    if (!providerRegionsObj) return [];

    const key =
      Object.keys(providerRegionsObj).find((k) => k.toLowerCase() === pCode) ||
      pCode;
    return providerRegionsObj[key] || [];
  }, [optionsData.regions, row.provider_code]);

  const validBenchmarks = useMemo(() => {
    const pCode = row.provider_code?.toLowerCase() || '';
    const mCode = row.module_code?.toLowerCase() || '';
    const filtered = optionsData.benchmarks.filter(
      (b) =>
        b.provider?.toLowerCase() === pCode &&
        b.module?.toLowerCase() === mCode,
    );
    if (filtered.length > 0) return filtered;

    // Fallback: match only provider if module string mismatch occurs
    return optionsData.benchmarks.filter(
      (b) => b.provider?.toLowerCase() === pCode,
    );
  }, [optionsData.benchmarks, row.provider_code, row.module_code]);

  const regionSelectOptions = useMemo(
    () =>
      validRegions.map((region) => ({
        label: region.title,
        value: region.value,
      })),
    [validRegions],
  );

  const benchmarkSelectOptions = useMemo(
    () =>
      validBenchmarks.map((bm) => ({
        label: bm.title,
        value: bm.value,
      })),
    [validBenchmarks],
  );

  /** Labels of benchmarks that already have an active scan for the selected region. */
  const inProgressBenchmarkLabels = useMemo(() => {
    if (!selectedRegion || !row.active_scans?.length) return new Set<string>();
    return new Set(
      row.active_scans
        .filter((s) => s.region === selectedRegion)
        .map((s) => s.benchmark),
    );
  }, [selectedRegion, row.active_scans]);

  const availableBenchmarkOptions = useMemo(
    () =>
      benchmarkSelectOptions.filter(
        (opt) => !inProgressBenchmarkLabels.has(opt.label),
      ),
    [benchmarkSelectOptions, inProgressBenchmarkLabels],
  );

  const blockedCount =
    benchmarkSelectOptions.length - availableBenchmarkOptions.length;

  useEffect(() => {
    if (isOpen) {
      const defaultRegion =
        row.region || (validRegions.length > 0 ? validRegions[0].value : '');
      setSelectedRegion(defaultRegion);
      setSelectedBenchmarks([]);
    }
  }, [isOpen, row.region, validRegions]);

  // When region changes mid-session, drop any selected benchmarks that are now blocked.
  useEffect(() => {
    setSelectedBenchmarks((prev) =>
      prev.filter((b) => !inProgressBenchmarkLabels.has(b.label)),
    );
  }, [inProgressBenchmarkLabels]);

  return (
    <DynamicModal
      isOpen={isOpen}
      toggle={toggle}
      title="Initiate Manual Scan"
      content={
        <div>
          <p>
            Please select Region and Benchmark to proceed with the manual scan
            for this Integration.
          </p>
          <div className="mb-3">
            <label className="form-label font-weight-bold">
              Region <span className="text-danger">*</span>
            </label>
            <Select
              className="react-select-container custom-360-dropdown text-left"
              classNamePrefix="react-select"
              options={regionSelectOptions}
              value={
                regionSelectOptions.find((o) => o.value === selectedRegion) ||
                null
              }
              onChange={(option) => setSelectedRegion(option?.value || '')}
              placeholder="Select Region"
              isSearchable={true}
            />
          </div>
          <div className="mb-3">
            <label className="form-label font-weight-bold">
              Benchmark <span className="text-danger">*</span>
            </label>
            <Select
              className="custom-360-dropdown text-left"
              classNamePrefix="react-select"
              options={availableBenchmarkOptions}
              value={selectedBenchmarks}
              onChange={(options) =>
                setSelectedBenchmarks(
                  options ? (options as BenchmarkOption[]) : [],
                )
              }
              placeholder="Select Benchmark(s)"
              isSearchable={true}
              isMulti
              closeMenuOnSelect={false}
              isClearable={true}
              hideSelectedOptions={true}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {blockedCount > 0 && (
              <div className="text-warning d-block mt-1">
                <i className="fa fa-exclamation-triangle mr-1" />
                {blockedCount} benchmark{blockedCount > 1 ? 's' : ''} excluded —
                scan already in progress for this region.
              </div>
            )}
          </div>
        </div>
      }
      size="md"
      footerActions={
        <>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!selectedRegion || selectedBenchmarks.length === 0}
            onClick={() => {
              toggle();
              onConfirm(
                selectedRegion,
                selectedBenchmarks.map((b) => b.value),
                selectedBenchmarks.map((b) => b.label),
              );
            }}
          >
            Initiate Scan
          </Button>
        </>
      }
    />
  );
};

export default ManualScanModal;
